import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  GripVertical,
  Plus,
  Trash2,
  Check,
  X,
  Megaphone,
} from "lucide-react";

const DAYS = [
  { id: "mon", label: "Mon" },
  { id: "tue", label: "Tue" },
  { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" },
  { id: "fri", label: "Fri" },
  { id: "sat", label: "Sat" },
  { id: "sun", label: "Sun" },
];

// ---- date helpers (Mondays only) ----
function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}
function fmtISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function prettyRange(monday) {
  const sunday = addDays(monday, 6);
  const sameMonth = monday.getMonth() === sunday.getMonth();
  const monthFmt = (d) => d.toLocaleString("en-GB", { month: "short" });
  const dayMon = monday.getDate();
  const daySun = sunday.getDate();
  const year = sunday.getFullYear();
  if (sameMonth) return `${dayMon}–${daySun} ${monthFmt(monday)} ${year}`;
  return `${dayMon} ${monthFmt(monday)} – ${daySun} ${monthFmt(sunday)} ${year}`;
}

function parseNames(str) {
  return (str || "")
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function normName(s) {
  return (s || "").normalize("NFKC").replace(/[\u200B\u00A0]/g, " ").trim().toLowerCase();
}

const VolunteerScheduleTab = ({ headers }) => {
  const [monday, setMonday] = useState(() => startOfWeek(new Date()));
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [volunteers, setVolunteers] = useState([]);
  const [editingCell, setEditingCell] = useState(null); // { taskIdx, dayId, value }
  const [editingNameIdx, setEditingNameIdx] = useState(null);
  const [mobileDay, setMobileDay] = useState(() => {
    const dow = new Date().getDay();
    return DAYS[(dow === 0 ? 6 : dow - 1)].id; // today's id (mon..sun)
  });
  const [mobileRowExpanded, setMobileRowExpanded] = useState(null);

  const weekStart = useMemo(() => fmtISO(monday), [monday]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [schedRes, volRes] = await Promise.all([
          axios.get(`${API}/admin/volunteer-schedules/${weekStart}`, { headers }),
          axios.get(`${API}/admin/volunteers`, { headers }),
        ]);
        if (cancelled) return;
        setTasks(schedRes.data?.tasks || []);
        setVolunteers(volRes.data || []);
      } catch (err) {
        if (!cancelled) toast.error("Couldn't load the weekly plan");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [weekStart, headers]);

  // ---- save helpers ----
  const saveTasks = async (next) => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/volunteer-schedules/${weekStart}`,
        { tasks: next },
        { headers },
      );
      setTasks(res.data?.tasks || next);
    } catch (err) {
      toast.error("Save failed — please retry");
    } finally {
      setSaving(false);
    }
  };

  // ---- mutations ----
  const setCell = (taskIdx, dayId, names) => {
    const next = tasks.map((t, i) =>
      i === taskIdx
        ? { ...t, assignments: { ...(t.assignments || {}), [dayId]: names } }
        : t,
    );
    saveTasks(next);
  };
  const renameTask = (taskIdx, name) => {
    const next = tasks.map((t, i) => (i === taskIdx ? { ...t, name } : t));
    saveTasks(next);
  };
  const toggleHighlight = (taskIdx) => {
    const next = tasks.map((t, i) =>
      i === taskIdx ? { ...t, highlight: !t.highlight } : t,
    );
    saveTasks(next);
  };
  const moveTask = (taskIdx, dir) => {
    const target = taskIdx + dir;
    if (target < 1 || target >= tasks.length) return; // keep row 0 ("Rooms") pinned
    if (taskIdx === 0) return;
    const next = [...tasks];
    [next[taskIdx], next[target]] = [next[target], next[taskIdx]];
    next.forEach((t, i) => (t.order = i));
    saveTasks(next);
  };
  const deleteTask = (taskIdx) => {
    if (taskIdx === 0) {
      toast.error("The Rooms row can't be removed");
      return;
    }
    if (!window.confirm(`Remove "${tasks[taskIdx]?.name}" from this week?`)) return;
    const next = tasks.filter((_, i) => i !== taskIdx).map((t, i) => ({ ...t, order: i }));
    saveTasks(next);
  };
  const addTask = () => {
    const name = window.prompt("New task name (e.g. Compost run, Bins out)");
    if (!name?.trim()) return;
    const next = [
      ...tasks,
      {
        name: name.trim(),
        highlight: false,
        order: tasks.length,
        assignments: DAYS.reduce((acc, d) => ({ ...acc, [d.id]: [] }), {}),
      },
    ];
    saveTasks(next);
  };

  // ---- week nav ----
  const gotoPrev = () => setMonday((m) => addDays(m, -7));
  const gotoNext = () => setMonday((m) => addDays(m, 7));
  const gotoToday = () => setMonday(startOfWeek(new Date()));

  // ---- copy to next week ----
  const copyToNextWeek = async () => {
    const target = fmtISO(addDays(monday, 7));
    if (!window.confirm(`Copy this week's plan to ${target}? Overwrites anything already there.`)) return;
    try {
      await axios.post(
        `${API}/admin/volunteer-schedules/${weekStart}/copy-to/${target}`,
        {},
        { headers },
      );
      toast.success(`Copied to week of ${target}`);
      setMonday(addDays(monday, 7));
    } catch (err) {
      toast.error("Copy failed");
    }
  };

  // ---- daily digest ----
  const [showDigest, setShowDigest] = useState(false);
  const [digestText, setDigestText] = useState("");
  const [digestLoading, setDigestLoading] = useState(false);
  const [pushing, setPushing] = useState(false);

  const openDigest = async () => {
    setShowDigest(true);
    setDigestLoading(true);
    try {
      const res = await axios.get(`${API}/admin/volunteer-daily-digest`, { headers });
      setDigestText(res.data?.text || "");
    } catch (e) {
      toast.error("Couldn't build today's roster");
    } finally {
      setDigestLoading(false);
    }
  };

  const copyDigest = async () => {
    try {
      await navigator.clipboard.writeText(digestText);
      toast.success("Copied — paste into WhatsApp ☕");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const pushDigest = async () => {
    if (!window.confirm("Send a push to every volunteer with this roster?")) return;
    setPushing(true);
    try {
      const res = await axios.post(`${API}/admin/volunteer-daily-digest/push`, {}, { headers });
      toast.success(`Push sent to ${res.data.sent}/${res.data.total} volunteers`);
    } catch (e) {
      toast.error("Push failed");
    } finally {
      setPushing(false);
    }
  };

  // ---- cell editor ----
  const openCellEditor = (taskIdx, dayId) => {
    const t = tasks[taskIdx];
    const current = (t.assignments?.[dayId] || []).join(", ");
    setEditingCell({ taskIdx, dayId, value: current });
  };
  const commitCellEditor = () => {
    if (!editingCell) return;
    const names = parseNames(editingCell.value);
    setCell(editingCell.taskIdx, editingCell.dayId, names);
    setEditingCell(null);
  };
  const insertVolunteerName = (name) => {
    if (!editingCell) return;
    const list = parseNames(editingCell.value);
    const target = normName(name);
    if (list.some((n) => normName(n) === target)) {
      setEditingCell({ ...editingCell, value: list.filter((n) => normName(n) !== target).join(", ") });
    } else {
      setEditingCell({ ...editingCell, value: [...list, name.trim()].join(", ") });
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-[#2E2A26]/40">Loading the weekly plan…</div>;
  }

  return (
    <div className="space-y-4" data-testid="volunteer-schedule-tab">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-2 sm:gap-3 bg-white rounded-2xl border border-stone-100 p-2.5 sm:p-4">
        <div className="flex items-center justify-center sm:justify-start gap-1">
          <Button variant="ghost" size="sm" onClick={gotoPrev} data-testid="vs-prev-week">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <button
            onClick={gotoToday}
            className="text-sm font-serif text-[#2E2A26] hover:text-[#C85A38] min-w-[140px] text-center"
            data-testid="vs-week-label"
          >
            {prettyRange(monday)}
          </button>
          <Button variant="ghost" size="sm" onClick={gotoNext} data-testid="vs-next-week">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {saving && <span className="text-[10px] text-[#2E2A26]/40 self-center">Saving…</span>}
          <Button
            variant="outline"
            size="sm"
            onClick={openDigest}
            className="text-[11px] sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
            data-testid="vs-open-digest"
          >
            <Megaphone className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Today&apos;s roster</span>
            <span className="sm:hidden ml-1">Roster</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={copyToNextWeek}
            className="text-[11px] sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
            data-testid="vs-copy-next"
          >
            <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Copy to next week</span>
            <span className="sm:hidden ml-1">Copy</span>
          </Button>
        </div>
      </div>

      {/* Volunteer chips */}
      {volunteers.length > 0 && (
        <div className="bg-[#F4ECE2] rounded-2xl border border-stone-100 p-2.5 sm:p-4">
          <div className="text-[10px] sm:text-xs text-[#2E2A26]/60 mb-1.5 sm:mb-2 leading-snug">
            Tap a name while editing a cell to add/remove. Mark guests as volunteers in the Guests tab.
          </div>
          <div className="flex flex-wrap gap-1.5">
            {volunteers.map((v) => {
              const active = editingCell &&
                parseNames(editingCell.value).some((n) => normName(n) === normName(v.name));
              return (
                <button
                  key={v.id}
                  onClick={() => insertVolunteerName(v.name)}
                  disabled={!editingCell}
                  className={`px-2.5 py-1 rounded-full text-xs border transition ${
                    !editingCell
                      ? "border-stone-200 text-[#2E2A26]/40 cursor-not-allowed"
                      : active
                        ? "bg-[#C85A38] border-[#C85A38] text-white"
                        : "bg-white border-stone-200 text-[#2E2A26] hover:border-[#C85A38]"
                  }`}
                  data-testid={`vs-volunteer-chip-${v.name}`}
                >
                  {v.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* MOBILE: day-picker + per-day task list */}
      <div className="sm:hidden space-y-3">
        {/* Day pills */}
        <div className="bg-white rounded-2xl border border-stone-100 p-2">
          <div className="grid grid-cols-7 gap-1">
            {DAYS.map((d, i) => {
              const dayDate = addDays(monday, i);
              const isSelected = d.id === mobileDay;
              const isToday =
                new Date().toDateString() === dayDate.toDateString();
              return (
                <button
                  key={d.id}
                  onClick={() => setMobileDay(d.id)}
                  className={`flex flex-col items-center py-1.5 rounded-xl text-xs transition-colors ${
                    isSelected
                      ? "bg-[#2E2A26] text-white"
                      : "text-[#2E2A26] hover:bg-[#F4ECE2]"
                  }`}
                  data-testid={`vs-mobile-day-${d.id}`}
                >
                  <span className="text-[10px] tracking-[0.15em]">{d.label.toUpperCase()}</span>
                  <span className={`text-sm font-medium ${isToday && !isSelected ? "text-[#C85A38]" : ""}`}>
                    {dayDate.getDate()}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Per-task cards for the selected day */}
        <div className="space-y-2">
          {tasks.map((task, taskIdx) => {
            const cellValue = task.assignments?.[mobileDay] || [];
            const isEditing =
              editingCell &&
              editingCell.taskIdx === taskIdx &&
              editingCell.dayId === mobileDay;
            const expanded = mobileRowExpanded === taskIdx;
            return (
              <div
                key={task.id || taskIdx}
                className={`bg-white rounded-2xl border border-stone-100 ${
                  task.highlight ? "ring-1 ring-[#C85A38]/30" : ""
                }`}
                data-testid={`vs-mobile-row-${taskIdx}`}
              >
                <div className="flex items-start gap-2 p-3">
                  <div className="flex-1 min-w-0">
                    {editingNameIdx === taskIdx ? (
                      <Input
                        autoFocus
                        defaultValue={task.name}
                        onBlur={(e) => {
                          renameTask(taskIdx, e.target.value || task.name);
                          setEditingNameIdx(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            renameTask(taskIdx, e.target.value || task.name);
                            setEditingNameIdx(null);
                          } else if (e.key === "Escape") {
                            setEditingNameIdx(null);
                          }
                        }}
                        className="h-7 text-xs"
                      />
                    ) : (
                      <div
                        className={`font-medium text-sm ${
                          task.highlight ? "text-[#C85A38]" : "text-[#2E2A26]"
                        }`}
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.05rem" }}
                      >
                        {task.name}
                      </div>
                    )}
                    {/* assignment area */}
                    <div className="mt-1.5">
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <Input
                            autoFocus
                            value={editingCell.value}
                            onChange={(e) =>
                              setEditingCell({ ...editingCell, value: e.target.value })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitCellEditor();
                              if (e.key === "Escape") setEditingCell(null);
                            }}
                            placeholder="Name(s) — comma separated"
                            className="h-8 text-xs"
                          />
                          <button onClick={commitCellEditor} className="text-[#8C9A7C] p-1" title="Save">
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingCell(null)} className="text-[#2E2A26]/40 p-1" title="Cancel">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => openCellEditor(taskIdx, mobileDay)}
                          className="w-full text-left px-2.5 py-1.5 bg-[#FBF7F1] hover:bg-[#F4ECE2] rounded-lg text-sm text-[#2E2A26] min-h-[32px]"
                          data-testid={`vs-mobile-cell-${taskIdx}`}
                        >
                          {cellValue.length > 0 ? (
                            cellValue.join(", ")
                          ) : (
                            <span className="text-[#2E2A26]/30">Tap to add names…</span>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  {taskIdx > 0 && (
                    <button
                      onClick={() => setMobileRowExpanded(expanded ? null : taskIdx)}
                      className="p-2 -mr-1 text-[#2E2A26]/40 hover:text-[#2E2A26]"
                      data-testid={`vs-mobile-row-menu-${taskIdx}`}
                      title="Row options"
                    >
                      <GripVertical className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {/* Expanded admin controls */}
                {expanded && taskIdx > 0 && (
                  <div className="border-t border-stone-100 px-3 py-2 flex items-center justify-between gap-2 text-xs">
                    <button
                      onClick={() => { setEditingNameIdx(taskIdx); setMobileRowExpanded(null); }}
                      className="text-[#2E2A26]/70 hover:text-[#2E2A26]"
                    >
                      Rename
                    </button>
                    <label className="flex items-center gap-1.5 text-[#2E2A26]/70">
                      <Switch
                        checked={!!task.highlight}
                        onCheckedChange={() => toggleHighlight(taskIdx)}
                        className="scale-75"
                      />
                      Red
                    </label>
                    <button
                      onClick={() => moveTask(taskIdx, -1)}
                      className="text-[#2E2A26]/70 hover:text-[#2E2A26]"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveTask(taskIdx, 1)}
                      className="text-[#2E2A26]/70 hover:text-[#2E2A26]"
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => { deleteTask(taskIdx); setMobileRowExpanded(null); }}
                      className="text-[#C85A38]/80 hover:text-[#C85A38]"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* DESKTOP: full 7-column grid */}
      <div className="hidden sm:block bg-white rounded-2xl border border-stone-100 overflow-x-auto">
        <table className="w-full text-xs sm:text-sm border-separate border-spacing-1 sm:border-spacing-1.5">
          <thead>
            <tr>
              <th className="bg-[#F4ECE2] rounded-md py-2 px-2 text-left text-[10px] sm:text-xs text-[#2E2A26]/60 font-medium min-w-[160px]">
                Task
              </th>
              {DAYS.map((d, i) => (
                <th
                  key={d.id}
                  className="bg-[#F4ECE2] rounded-md py-2 px-2 text-center text-[10px] sm:text-xs text-[#2E2A26]/60 font-medium min-w-[100px]"
                >
                  {d.label}
                  <div className="text-[9px] text-[#2E2A26]/40 mt-0.5">
                    {addDays(monday, i).getDate()}
                  </div>
                </th>
              ))}
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task, taskIdx) => (
              <tr key={task.id || taskIdx}>
                <td
                  className={`bg-[#FBF7F1] rounded-md px-2 py-2 align-middle text-xs sm:text-sm ${
                    task.highlight ? "text-[#C85A38] font-medium" : "text-[#2E2A26]"
                  }`}
                  data-testid={`vs-task-row-${taskIdx}`}
                >
                  <div className="flex items-center gap-1.5">
                    {taskIdx > 0 && (
                      <GripVertical className="w-3 h-3 text-[#2E2A26]/30 hidden sm:block" />
                    )}
                    {editingNameIdx === taskIdx ? (
                      <Input
                        autoFocus
                        defaultValue={task.name}
                        onBlur={(e) => {
                          renameTask(taskIdx, e.target.value || task.name);
                          setEditingNameIdx(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            renameTask(taskIdx, e.target.value || task.name);
                            setEditingNameIdx(null);
                          } else if (e.key === "Escape") {
                            setEditingNameIdx(null);
                          }
                        }}
                        className="h-7 text-xs"
                      />
                    ) : (
                      <button
                        onClick={() => taskIdx > 0 && setEditingNameIdx(taskIdx)}
                        className="text-left flex-1 hover:text-[#C85A38]"
                        title={taskIdx === 0 ? "The Rooms row is fixed" : "Click to rename"}
                      >
                        {task.name}
                      </button>
                    )}
                  </div>
                  {taskIdx > 0 && (
                    <div className="flex items-center gap-2 mt-1 pl-4">
                      <label className="flex items-center gap-1 text-[10px] text-[#2E2A26]/40">
                        <Switch
                          checked={!!task.highlight}
                          onCheckedChange={() => toggleHighlight(taskIdx)}
                          className="scale-75"
                        />
                        red
                      </label>
                      <button
                        onClick={() => moveTask(taskIdx, -1)}
                        className="text-[10px] text-[#2E2A26]/40 hover:text-[#2E2A26]"
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveTask(taskIdx, 1)}
                        className="text-[10px] text-[#2E2A26]/40 hover:text-[#2E2A26]"
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => deleteTask(taskIdx)}
                        className="text-[10px] text-[#C85A38]/60 hover:text-[#C85A38]"
                        title="Delete row"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </td>
                {DAYS.map((d) => {
                  const isEditing =
                    editingCell &&
                    editingCell.taskIdx === taskIdx &&
                    editingCell.dayId === d.id;
                  const cellValue = task.assignments?.[d.id] || [];
                  return (
                    <td
                      key={d.id}
                      className="bg-[#FBF7F1] rounded-md px-1.5 py-1.5 align-middle"
                      data-testid={`vs-cell-${taskIdx}-${d.id}`}
                    >
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <Input
                            autoFocus
                            value={editingCell.value}
                            onChange={(e) =>
                              setEditingCell({ ...editingCell, value: e.target.value })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitCellEditor();
                              if (e.key === "Escape") setEditingCell(null);
                            }}
                            placeholder="Name(s)"
                            className="h-7 text-xs px-1.5"
                          />
                          <button
                            onClick={commitCellEditor}
                            className="text-[#8C9A7C] hover:text-[#6C7D5C]"
                            title="Save"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setEditingCell(null)}
                            className="text-[#2E2A26]/40 hover:text-[#2E2A26]"
                            title="Cancel"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => openCellEditor(taskIdx, d.id)}
                          className="w-full min-h-[28px] text-center text-xs sm:text-sm hover:bg-white rounded px-1 py-0.5 text-[#2E2A26]"
                          data-testid={`vs-cell-btn-${taskIdx}-${d.id}`}
                        >
                          {cellValue.length > 0 ? (
                            cellValue.join(" ")
                          ) : (
                            <span className="text-[#2E2A26]/20">+</span>
                          )}
                        </button>
                      )}
                    </td>
                  );
                })}
                <td className="w-12"></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={addTask}
        className="text-xs sm:text-sm"
        data-testid="vs-add-task"
      >
        <Plus className="w-4 h-4 mr-1.5" /> Add task
      </Button>

      {/* Today's roster digest dialog */}
      <Dialog open={showDigest} onOpenChange={setShowDigest}>
        <DialogContent className="max-w-lg" data-testid="vs-digest-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#2E2A26]">
              Today&apos;s Team Roster
            </DialogTitle>
          </DialogHeader>
          {digestLoading ? (
            <div className="py-8 text-center text-[#2E2A26]/40">Building…</div>
          ) : (
            <>
              <textarea
                value={digestText}
                onChange={(e) => setDigestText(e.target.value)}
                rows={14}
                className="w-full text-xs font-mono bg-[#FBF7F1] border border-stone-200 rounded-xl p-3 leading-relaxed"
                data-testid="vs-digest-text"
              />
              <p className="text-[10px] text-[#2E2A26]/50 mt-1">
                You can edit the text above before copying or pushing.
              </p>
              <div className="flex flex-wrap gap-2 mt-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyDigest}
                  data-testid="vs-digest-copy"
                >
                  <Copy className="w-4 h-4 mr-1.5" /> Copy for WhatsApp
                </Button>
                <Button
                  size="sm"
                  onClick={pushDigest}
                  disabled={pushing}
                  className="bg-[#8C9A7C] hover:bg-[#6F7E5F] text-white"
                  data-testid="vs-digest-push"
                >
                  <Megaphone className="w-4 h-4 mr-1.5" />
                  {pushing ? "Pushing…" : "Push to all volunteers"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VolunteerScheduleTab;
